import { JsonFormErrorFloatingPane } from '../JsonFormErrorFloatingPane';
import { rankWith, isBooleanControl, or, isEnumControl, schemaMatches, and } from '@jsonforms/core';
import type { ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { cn } from '@thinkalpha/ui-core/utils';
import React, { useCallback, useMemo } from 'react';
import { Errors, Description, Label } from 'src/components/JsonForm/helpers';
import { fakeRequired, lightStyle } from 'src/components/JsonForm/ThinkalphaTheme';
import { SelectTrigger, SelectValue, SelectContent, SelectItem, Select, Switch } from 'src/components/ui';
import {
    INSTANT_REMOVAL_FROM_ORDER_DATA_VALUE,
    INSTANT_REMOVAL_FROM_ORDER_DATA_LABEL,
} from 'src/lib/orders/removePropsFromOrderData';

const truthyValue = (value: any) =>
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'y' ||
    value === 'Y' ||
    value === 'yes' ||
    value === 'YES' ||
    value === 'Yes';

const falsyValue = (value: any) =>
    value === false ||
    value === 0 ||
    value === '0' ||
    value === 'n' ||
    value === 'N' ||
    value === 'no' ||
    value === 'NO' ||
    value === 'No';

export const BooleanControlComponent = ({
    id,
    path,
    data,
    handleChange,
    description,
    errors,
    label,
    required,
    visible,
    schema,
}: ControlProps) => {
    const hasErrors = errors.length > 0;

    const updateValue = useCallback(
        (checked: boolean): any =>
            schema.enum
                ? checked
                    ? schema.enum.find((val) => truthyValue(val)) || true
                    : schema.enum.find((val) => falsyValue(val)) || false
                : checked,
        [schema.enum],
    );

    const calculatedSwitchValue = useMemo(() => {
        return schema.enum ? truthyValue(data) : data;
    }, [schema.enum, data]);

    const calculatedSelectValue = useMemo(() => {
        if (data) {
            return '1';
        } else if (data === false) {
            return '0';
        }
        return INSTANT_REMOVAL_FROM_ORDER_DATA_VALUE;
    }, [data]);

    const handleCheckedChange = useCallback(
        (checked: boolean) => handleChange(path, updateValue(checked)),
        [updateValue, handleChange, path],
    );

    const handleSelectChange = useCallback(
        (val: string) => {
            handleChange(
                path,
                truthyValue(val) ? true : falsyValue(val) ? false : INSTANT_REMOVAL_FROM_ORDER_DATA_VALUE,
            );
        },
        [handleChange, path],
    );

    if (!visible) {
        return null;
    }

    if (fakeRequired.includes(path)) {
        required = true; // make it look like required
    }

    const isLight = lightStyle.includes(path);

    return (
        <>
            {required && (
                <JsonFormErrorFloatingPane hasErrors={hasErrors} errors={<Errors errors={errors} />}>
                    <div className="flex items-center gap-2">
                        <Switch
                            id={id}
                            checked={calculatedSwitchValue}
                            onCheckedChange={handleCheckedChange}
                            color={isLight ? 'light' : 'positive'}
                            size="sm"
                            className={cn(
                                hasErrors &&
                                    (isLight
                                        ? 'data-[state=unchecked]:bg-[#fbcdc1]'
                                        : 'data-[state=unchecked]:bg-[#4C1919]'),
                            )}
                        />

                        <Label htmlFor={id} label={label} required={required} />
                        <Description description={description} />
                    </div>
                </JsonFormErrorFloatingPane>
            )}
            {!required && (
                <div>
                    <div className="flex items-center gap-2">
                        <Label label={label} required={required} />
                        <Description description={description} />
                    </div>
                    <JsonFormErrorFloatingPane hasErrors={hasErrors} errors={<Errors errors={errors} />}>
                        <Select value={calculatedSelectValue} onValueChange={handleSelectChange}>
                            <SelectTrigger dimensions="sm" variant={isLight ? 'light' : 'default'}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={INSTANT_REMOVAL_FROM_ORDER_DATA_VALUE}>
                                    {INSTANT_REMOVAL_FROM_ORDER_DATA_LABEL}
                                </SelectItem>
                                <SelectItem value="1">Yes</SelectItem>
                                <SelectItem value="0">No</SelectItem>
                            </SelectContent>
                        </Select>
                    </JsonFormErrorFloatingPane>
                </div>
            )}
        </>
    );
};

const isEnumBoolean = and(
    isEnumControl,
    schemaMatches((schema) => {
        return Boolean(
            schema.enum &&
            schema.enum.length === 2 &&
            ((schema.enum.includes(true) && schema.enum.includes(false)) ||
                (schema.enum.includes(0) && schema.enum.includes(1)) ||
                (schema.enum.includes('0') && schema.enum.includes('1')) ||
                (schema.enum.includes('y') && schema.enum.includes('n')) ||
                (schema.enum.includes('Y') && schema.enum.includes('N')) ||
                (schema.enum.includes('no') && schema.enum.includes('yes')) ||
                (schema.enum.includes('No') && schema.enum.includes('Yes'))),
        );
    }),
);

export const BooleanControlTester = rankWith(
    10, // increase rank as needed
    or(isBooleanControl, isEnumBoolean),
);

export const BooleanControl = withJsonFormsControlProps(BooleanControlComponent);
